/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import heroBg from "@/assets/images/hero-bg.jpg";
import watsonAeroLogo from "@/assets/images/watson-aero-logo.png";
import engineShowcase from "@/assets/images/engine-showcase.jpg";
import continentalMotorsLogo from "@/assets/images/continental-motors-logo.png";
import cirrusLycomingLogos from "@/assets/images/cirrus-lycoming-logos.png";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Hero section images
  "/background-images/865779286.jpg": heroBg,
  "/images/hero-bg.jpg": heroBg,
  "/assets/images/hero-bg.jpg": heroBg,
  "/uploads/6/0/7/8/60782893/background-images/865779286.jpg": heroBg,
  
  // Watson Aero logo
  "/published/watson-aero-llc-copy.png": watsonAeroLogo,
  "/images/watson-aero-logo.png": watsonAeroLogo,
  "/assets/images/watson-aero-logo.png": watsonAeroLogo,
  "/uploads/6/0/7/8/60782893/published/watson-aero-llc-copy.png": watsonAeroLogo,
  
  // Engine showcase image
  "/uploads/6/0/7/8/60782893/7fc27244-3325-47fd-a9bb-82faba697766_orig.jpeg": engineShowcase,
  "/images/engine-showcase.jpg": engineShowcase,
  "/assets/images/engine-showcase.jpg": engineShowcase,
  
  // Partner logo images
  "/uploads/6/0/7/8/60782893/cmi.png": continentalMotorsLogo,
  "/images/continental-motors-logo.png": continentalMotorsLogo,
  "/assets/images/continental-motors-logo.png": continentalMotorsLogo,
  
  "/uploads/6/0/7/8/60782893/1464022079.png": cirrusLycomingLogos,
  "/images/cirrus-lycoming-logos.png": cirrusLycomingLogos,
  "/assets/images/cirrus-lycoming-logos.png": cirrusLycomingLogos,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
